import React, { useEffect, useMemo, useState } from "react";
import { Select } from "antd";
import { ReactComponent as Close } from "../../../newLayout/assets/svgIcon/listView/Close.svg";
import {
  FilterOverlay,
  FilterDropdownPanel,
  PanelHeader,
  CloseBtn,
  PanelBody,
  StyledSelect,
  SelectAllRow,
  OptionRow,
  OptionsListContainer,
  ActionRow,
  CancelBtn,
  ApplyBtn,
} from "./index.styles";

const { Option } = Select;

// ---------------------------------------------------------------------------
// FilterDropdown
// ---------------------------------------------------------------------------
/**
 * FilterDropdown
 *
 * Props:
 *  - isOpen        : boolean
 *  - onClose       : () => void
 *  - label         : string — column display name, e.g. "Customer Code"
 *  - options       : Array<{ value: string|number, label: string }>
 *                    static list (fallback)
 *  - useSelectionQuery : RTK Query hook function (optional)
 *  - selectOptions : mapper function for query response (optional)
 *  - activeValues  : string[]   — currently active filter values (comma-joined in API)
 *  - onApply       : (values: string[]) => void  — called with selected value array
 *  - onClear       : () => void  — called when filter is cleared/reset
 */
const FilterDropdown = ({
  isOpen,
  onClose,
  label,
  options: propOptions = [],
  useSelectionQuery = () => ({ data: null }),
  selectOptions,
  activeValues = [],
  onApply,
  onClear,
}) => {
  const [searchText, setSearchText] = useState("");
  const [selected, setSelected] = useState(activeValues);

  // Call hook conditionally (but stably per instance render path)
  const { data } = useSelectionQuery();

  const options = useMemo(() => {
    if (useSelectionQuery && selectOptions) {
      return selectOptions(data);
    }
    return propOptions;
  }, [useSelectionQuery, selectOptions, data, propOptions]);

  // Reset internal state each time the panel opens
  useEffect(() => {
    if (isOpen) {
      setSelected(activeValues);
      setSearchText("");
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = e => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const filtered = useMemo(() => {
    if (!searchText) return options;
    const q = searchText.toLowerCase();
    return options.filter(o => String(o.label).toLowerCase().includes(q));
  }, [options, searchText]);

  const allVisibleSelected =
    filtered.length > 0 && filtered.every(o => selected.includes(String(o.value)));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      // deselect all visible
      const visibleValues = new Set(filtered.map(o => String(o.value)));
      setSelected(prev => prev.filter(v => !visibleValues.has(v)));
    } else {
      // select all visible (merge with existing)
      const visibleValues = filtered.map(o => String(o.value));
      setSelected(prev => Array.from(new Set([...prev, ...visibleValues])));
    }
  };

  const toggleItem = value => {
    const strVal = String(value);
    setSelected(prev =>
      prev.includes(strVal) ? prev.filter(v => v !== strVal) : [...prev, strVal],
    );
  };

  const handleApply = () => {
    onApply(selected);
    onClose();
  };

  const handleClear = () => {
    setSelected([]);
    onClear();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <FilterOverlay onClick={onClose} />
      <FilterDropdownPanel>
        {/* Header */}
        <PanelHeader>
          Filter {label}
          <CloseBtn onClick={onClose}>
            <Close />
          </CloseBtn>
        </PanelHeader>

        {/* Search box using Ant Design Select in search mode */}
        <PanelBody>
          <StyledSelect
            showSearch
            mode="multiple"
            placeholder={`Search ${label}…`}
            value={selected}
            onChange={vals => setSelected(vals)}
            // onChange={vals => setSelected(vals.map(String))}
            searchValue={searchText}
            onSearch={e => setSearchText(e)}
            filterOption={false}
            maxTagCount="responsive"
            dropdownStyle={{ display: "none" }} // hide native dropdown
            open={true}
            autoClearSearchValue={false}
            allowClear
            onClear={() => setSelected([])}
          >
            {options.map(o => (
              <Option key={String(o.value)} value={String(o.value)}>
                {o.label}
              </Option>
            ))}
          </StyledSelect>

          {/* Custom checkbox list */}
          <OptionsListContainer>
            {/* Select All row */}
            <SelectAllRow onClick={toggleSelectAll}>
              <input type="checkbox" readOnly checked={allVisibleSelected} onChange={() => {}} />
              Select All
              {filtered.length < options.length && (
                <span style={{ color: "#9da3b4", fontSize: 11 }}>({filtered.length} shown)</span>
              )}
            </SelectAllRow>

            {filtered.length === 0 ? (
              <div
                style={{
                  padding: "12px 14px",
                  color: "#9da3b4",
                  fontSize: 13,
                  textAlign: "center",
                }}
              >
                No results
              </div>
            ) : (
              filtered.map(o => (
                <OptionRow key={o.id + o.value}>
                  <input
                    type="checkbox"
                    readOnly
                    // checked={selected.includes(String(o.value))}
                    checked={selected.includes(o.value)}
                    onChange={() => toggleItem(o.value)}
                  />
                  <span style={{ cursor: "pointer", flex: 1 }} onClick={() => toggleItem(o.value)}>
                    {o.label}
                  </span>
                </OptionRow>
              ))
            )}
          </OptionsListContainer>
        </PanelBody>

        {/* Actions */}
        <ActionRow>
          <CancelBtn onClick={handleClear}>Clear</CancelBtn>
          <ApplyBtn
            $active={selected.length > 0}
            onClick={handleApply}
            disabled={selected.length === 0}
          >
            Apply Filter
          </ApplyBtn>
        </ActionRow>
      </FilterDropdownPanel>
    </>
  );
};

export default FilterDropdown;
